import React, { useState, useEffect, useMemo } from "react";
import "./Projects.css";
import ProjectModal from "./ProjectModal";
import ImageModal from "./ImageSlider/Modal";
import ScreenHeading from "../../Utilities/ScreenHeading/ScreenHeading";
import ScrollService from "../../Utilities/ScrollService";
import Animations from "../../Utilities/Animations";
import projects from "./ProjectsData";
import { getAllTags, filterProjects } from "../../Utilities/projectsFilter";

const FEATURED_IDS = [
  "prepquest",
  "provision",
  "groupomania",
  "beyond",
  "shopping-list-modern",
  "portfolio-v2",
];

const ProjectCard = ({ project, openProjectModal }) => (
  <div className="project-card">
    <div className="project-card-image">
      {project.slides && project.slides.length > 0 ? (
        <img
          src={project.slides[0].url}
          alt={`${project.title} - aperçu`}
          loading="lazy"
          onClick={() => openProjectModal(project)}
        />
      ) : (
        <div
          className="project-card-placeholder"
          onClick={() => openProjectModal(project)}
        />
      )}
    </div>
    <div className="project-card-body">
      <h3 className="project-card-title">{project.title}</h3>
      {project.shortDescription && (
        <p className="project-card-desc">{project.shortDescription}</p>
      )}
      <div className="project-card-tags">
        {project.tags.slice(0, 4).map((tag, i) => (
          <span key={i} className="project-tag">
            {tag}
          </span>
        ))}
      </div>
      <div className="project-card-actions">
        {project.githubLink && (
          <a
            href={project.githubLink}
            target="_blank"
            rel="noopener noreferrer"
            className="project-link-btn"
          >
            <i className="fa fa-github"></i> GitHub
          </a>
        )}
        {project.demoLink && (
          <a
            href={project.demoLink}
            target="_blank"
            rel="noopener noreferrer"
            className="project-link-btn project-link-btn-accent"
          >
            Demo
          </a>
        )}
        <button
          onClick={() => openProjectModal(project)}
          className="project-link-btn project-link-btn-details"
        >
          Détails
        </button>
      </div>
    </div>
  </div>
);

export default function Projects(props) {
  const [showProjectModal, setShowProjectModal] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);
  const [currentProject, setCurrentProject] = useState(null);
  const [modalImageUrl, setModalImageUrl] = useState("");
  const [currentSlides, setCurrentSlides] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showAll, setShowAll] = useState(false);
  const [selectedTag, setSelectedTag] = useState("ALL");

  const allTags = useMemo(() => getAllTags(projects), []);

  const featuredProjects = useMemo(
    () => projects.filter((p) => FEATURED_IDS.includes(p.id)),
    [],
  );

  const otherProjects = useMemo(
    () => projects.filter((p) => !FEATURED_IDS.includes(p.id)),
    [],
  );

  const filteredAll = useMemo(
    () => filterProjects(projects, selectedTag),
    [selectedTag],
  );

  useEffect(() => {
    const subscription = ScrollService.currentScreenFadeIn.subscribe(
      (screen) => {
        if (screen.fadeInScreen !== props.id) return;
        Animations.animations.fadeInScreen(props.id);
      },
    );
    return () => subscription.unsubscribe();
  }, [props.id]);

  const openProjectModal = (project) => {
    setCurrentProject(project);
    setShowProjectModal(true);
  };

  const closeProjectModal = () => {
    setShowProjectModal(false);
    setCurrentProject(null);
  };

  // eslint-disable-next-line no-unused-vars
  const openImageModal = (slides, index) => {
    setCurrentSlides(slides);
    setCurrentIndex(index);
    setModalImageUrl(slides[index].url);
    setShowImageModal(true);
  };

  const closeImageModal = () => {
    setShowImageModal(false);
    setModalImageUrl("");
  };

  const goToPrevious = () => {
    const newIndex =
      currentIndex === 0 ? currentSlides.length - 1 : currentIndex - 1;
    setCurrentIndex(newIndex);
    setModalImageUrl(currentSlides[newIndex].url);
  };

  const goToNext = () => {
    const newIndex =
      currentIndex === currentSlides.length - 1 ? 0 : currentIndex + 1;
    setCurrentIndex(newIndex);
    setModalImageUrl(currentSlides[newIndex].url);
  };

  const displayedProjects = showAll ? filteredAll : featuredProjects;

  return (
    <>
      <div className="projects-section fade-in" id={props.id || ""}>
        <ScreenHeading
          title={"Projets sélectionnés"}
          subHeading={"Applications full-stack, APIs et interfaces modernes"}
        />

        {showAll && (
          <div className="projects-filter">
            <div className="projects-filter-count">
              {filteredAll.length} projet{filteredAll.length > 1 ? "s" : ""}
            </div>
            <select
              className="projects-filter-select"
              value={selectedTag}
              onChange={(e) => setSelectedTag(e.target.value)}
              aria-label="Filtrer les projets par technologie"
            >
              <option value="ALL">Tous</option>
              {allTags.map((tag) => (
                <option key={tag} value={tag}>
                  {tag}
                </option>
              ))}
            </select>
          </div>
        )}

        <div className="projects-grid">
          {displayedProjects.map((project, index) => (
            <ProjectCard
              key={project.id || index}
              project={project}
              openProjectModal={openProjectModal}
            />
          ))}
        </div>

        {!showAll && otherProjects.length > 0 && (
          <div className="projects-show-more">
            <button className="show-more-btn" onClick={() => setShowAll(true)}>
              Voir plus de projets
            </button>
          </div>
        )}

        {showAll && (
          <div className="projects-show-more">
            <button
              className="show-more-btn"
              onClick={() => {
                setShowAll(false);
                setSelectedTag("ALL");
              }}
            >
              Voir moins
            </button>
          </div>
        )}
      </div>

      <ProjectModal
        showModal={showProjectModal}
        closeModal={closeProjectModal}
        project={currentProject}
      />

      <ImageModal
        showModal={showImageModal}
        closeModal={closeImageModal}
        imageUrl={modalImageUrl}
        goToPrevious={goToPrevious}
        goToNext={goToNext}
      />
    </>
  );
}
